import {
  addPlatformProfileAsContactRequestSchema,
  addPlatformProfileAsContactResponseSchema,
  contactBulkDeleteRequestSchema,
  contactBulkDeleteResponseSchema,
  contactBulkGroupsRequestSchema,
  contactBulkGroupsResponseSchema,
  contactDetailSchema,
  contactGroupListResponseSchema,
  contactGroupSummarySchema,
  contactImportCapacityPreviewSchema,
  contactImportExecuteRequestSchema,
  contactImportPreviewRequestSchema,
  contactImportPreviewResponseSchema,
  contactImportResultSchema,
  contactImportJobConfirmRequestSchema,
  contactImportJobEnvelopeSchema,
  contactImportJobDtoSchema,
  contactImportActiveJobResponseSchema,
  contactListQuerySchema,
  contactListResponseSchema,
  contactMergeExecuteRequestSchema,
  contactMergeExecuteResponseSchema,
  contactMergePreviewRequestSchema,
  contactMergePreviewResponseSchema,
  contactPhoneMatchResponseSchema,
  createContactGroupRequestSchema,
  createContactRequestSchema,
  createContactResponseSchema,
  updateContactGroupRequestSchema,
  updateContactRequestSchema,
  updateContactResponseSchema,
  type AddPlatformProfileAsContactRequest,
  type AddPlatformProfileAsContactResponse,
  type ContactDetail,
  type ContactGroupSummary,
  type ContactBulkDeleteRequest,
  type ContactBulkDeleteResponse,
  type ContactBulkGroupsMode,
  type ContactBulkGroupsRequest,
  type ContactBulkGroupsResponse,
  type ContactImportCapacityPreview,
  type ContactImportExecuteRequest,
  type ContactImportPreviewRequest,
  type ContactImportPreviewResponse,
  type ContactImportResult,
  type ContactImportJobConfirmRequest,
  type ContactImportJobDto,
  type ContactImportJobEnvelope,
  type ContactImportActiveJobResponse,
  type ContactListQuery,
  type ContactListResponse,
  type ContactMergeExecuteRequest,
  type ContactMergeExecuteResponse,
  type ContactMergePreviewRequest,
  type ContactMergePreviewResponse,
  type ContactPhoneMatchResponse,
  type CreateContactRequest,
  type UpdateContactRequest,
} from "@multi-chat/shared";
import { API_BASE_URL, apiDelete, apiGet, apiPatch, apiPost, apiPostFormData } from "./client.js";

function buildContactListQuery(params: ContactListQuery): string {
  const parsed = contactListQuerySchema.parse(params);
  const search = new URLSearchParams();
  if (parsed.page != null) search.set("page", String(parsed.page));
  if (parsed.pageSize != null) search.set("pageSize", String(parsed.pageSize));
  if (parsed.search) search.set("search", parsed.search);
  if (parsed.groupId != null) search.set("groupId", String(parsed.groupId));
  if (parsed.hasEmail) search.set("hasEmail", parsed.hasEmail);
  if (parsed.hasPhone) search.set("hasPhone", parsed.hasPhone);
  if (parsed.platform) search.set("platform", parsed.platform);
  if (parsed.createdFrom) search.set("createdFrom", parsed.createdFrom);
  if (parsed.createdTo) search.set("createdTo", parsed.createdTo);
  if (parsed.sort) search.set("sort", parsed.sort);
  if (parsed.includeDeleted) search.set("includeDeleted", "true");
  if (parsed.phoneCountryIso) search.set("phoneCountryIso", parsed.phoneCountryIso);
  const qs = search.toString();
  return qs.length > 0 ? `?${qs}` : "";
}

export async function listContacts(
  organizationId: number,
  query: ContactListQuery,
  signal?: AbortSignal,
): Promise<ContactListResponse> {
  return contactListResponseSchema.parse(
    await apiGet(
      `/organizations/${organizationId}/contacts${buildContactListQuery(query)}`,
      signal,
    ),
  );
}

export async function getContact(
  organizationId: number,
  contactId: number,
  signal?: AbortSignal,
): Promise<ContactDetail> {
  return contactDetailSchema.parse(
    await apiGet(`/organizations/${organizationId}/contacts/${contactId}`, signal),
  );
}

export async function createContact(
  organizationId: number,
  body: CreateContactRequest,
  signal?: AbortSignal,
): Promise<ContactDetail> {
  const payload = createContactRequestSchema.parse(body);
  const response = createContactResponseSchema.parse(
    await apiPost(`/organizations/${organizationId}/contacts`, payload, signal),
  );
  return response.contact;
}

export async function updateContact(
  organizationId: number,
  contactId: number,
  body: UpdateContactRequest,
  signal?: AbortSignal,
): Promise<ContactDetail> {
  const payload = updateContactRequestSchema.parse(body);
  const response = updateContactResponseSchema.parse(
    await apiPatch(`/organizations/${organizationId}/contacts/${contactId}`, payload, signal),
  );
  return response.contact;
}

export async function deleteContact(
  organizationId: number,
  contactId: number,
  signal?: AbortSignal,
): Promise<void> {
  await apiDelete(`/organizations/${organizationId}/contacts/${contactId}`, signal);
}

export async function restoreContact(
  organizationId: number,
  contactId: number,
  signal?: AbortSignal,
): Promise<ContactDetail> {
  const response = updateContactResponseSchema.parse(
    await apiPost(`/organizations/${organizationId}/contacts/${contactId}/restore`, {}, signal),
  );
  return response.contact;
}

export async function listContactGroups(
  organizationId: number,
  signal?: AbortSignal,
): Promise<ContactGroupSummary[]> {
  const response = contactGroupListResponseSchema.parse(
    await apiGet(`/organizations/${organizationId}/contact-groups`, signal),
  );
  return response.items;
}

export async function createContactGroup(
  organizationId: number,
  name: string,
  signal?: AbortSignal,
): Promise<ContactGroupSummary> {
  const payload = createContactGroupRequestSchema.parse({ name });
  return contactGroupSummarySchema.parse(
    await apiPost(`/organizations/${organizationId}/contact-groups`, payload, signal),
  );
}

export async function renameContactGroup(
  organizationId: number,
  groupId: number,
  name: string,
  signal?: AbortSignal,
): Promise<ContactGroupSummary> {
  const payload = updateContactGroupRequestSchema.parse({ name });
  return contactGroupSummarySchema.parse(
    await apiPatch(`/organizations/${organizationId}/contact-groups/${groupId}`, payload, signal),
  );
}

export async function deleteContactGroup(
  organizationId: number,
  groupId: number,
  signal?: AbortSignal,
): Promise<void> {
  await apiDelete(`/organizations/${organizationId}/contact-groups/${groupId}`, signal);
}

export async function bulkDeleteContacts(
  organizationId: number,
  body: ContactBulkDeleteRequest,
  signal?: AbortSignal,
): Promise<ContactBulkDeleteResponse> {
  const payload = contactBulkDeleteRequestSchema.parse(body);
  return contactBulkDeleteResponseSchema.parse(
    await apiPost(`/organizations/${organizationId}/contacts/bulk-delete`, payload, signal),
  );
}

export async function bulkUpdateContactGroups(
  organizationId: number,
  body: ContactBulkGroupsRequest,
  signal?: AbortSignal,
): Promise<ContactBulkGroupsResponse> {
  const payload = contactBulkGroupsRequestSchema.parse(body);
  return contactBulkGroupsResponseSchema.parse(
    await apiPost(`/organizations/${organizationId}/contacts/bulk-groups`, payload, signal),
  );
}

export async function bulkDeleteContactsInGroup(
  organizationId: number,
  groupId: number,
  signal?: AbortSignal,
): Promise<ContactBulkDeleteResponse> {
  return contactBulkDeleteResponseSchema.parse(
    await apiPost(
      `/organizations/${organizationId}/contact-groups/${groupId}/contacts/bulk-delete`,
      {},
      signal,
    ),
  );
}

export function contactImportTemplateUrl(organizationId: number): string {
  return `${API_BASE_URL}/organizations/${organizationId}/contacts/import/template`;
}

export async function previewContactsImport(
  organizationId: number,
  body: ContactImportPreviewRequest,
  signal?: AbortSignal,
): Promise<ContactImportPreviewResponse> {
  const payload = contactImportPreviewRequestSchema.parse(body);
  return contactImportPreviewResponseSchema.parse(
    await apiPost(`/organizations/${organizationId}/contacts/import/preview`, payload, signal),
  );
}

export async function importContactsCsv(
  organizationId: number,
  body: ContactImportExecuteRequest,
  signal?: AbortSignal,
): Promise<ContactImportResult> {
  const payload = contactImportExecuteRequestSchema.parse(body);
  return contactImportResultSchema.parse(
    await apiPost(`/organizations/${organizationId}/contacts/import`, payload, signal),
  );
}

export async function getActiveContactImportJob(
  organizationId: number,
  signal?: AbortSignal,
): Promise<ContactImportActiveJobResponse> {
  return contactImportActiveJobResponseSchema.parse(
    await apiGet(`/organizations/${organizationId}/contacts/import/active`, signal),
  );
}

export async function uploadContactImportFile(
  organizationId: number,
  file: File,
  signal?: AbortSignal,
): Promise<ContactImportJobEnvelope> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("fileSizeBytes", String(file.size));
  return contactImportJobEnvelopeSchema.parse(
    await apiPostFormData(`/organizations/${organizationId}/contacts/import/uploads`, formData, signal),
  );
}

export async function getContactImportJob(
  organizationId: number,
  jobId: string,
  signal?: AbortSignal,
): Promise<ContactImportJobDto> {
  return contactImportJobDtoSchema.parse(
    await apiGet(`/organizations/${organizationId}/contacts/import/jobs/${jobId}`, signal),
  );
}

export async function previewContactImportJobCapacity(
  organizationId: number,
  jobId: string,
  body: ContactImportJobConfirmRequest,
  signal?: AbortSignal,
): Promise<ContactImportCapacityPreview> {
  const payload = contactImportJobConfirmRequestSchema.parse(body);
  return contactImportCapacityPreviewSchema.parse(
    await apiPost(
      `/organizations/${organizationId}/contacts/import/jobs/${jobId}/capacity`,
      payload,
      signal,
    ),
  );
}

export async function confirmContactImportJob(
  organizationId: number,
  jobId: string,
  body: ContactImportJobConfirmRequest,
  signal?: AbortSignal,
): Promise<ContactImportJobDto> {
  const payload = contactImportJobConfirmRequestSchema.parse(body);
  return contactImportJobDtoSchema.parse(
    await apiPost(
      `/organizations/${organizationId}/contacts/import/jobs/${jobId}/confirm`,
      payload,
      signal,
    ),
  );
}

export async function cancelContactImportJob(
  organizationId: number,
  jobId: string,
  signal?: AbortSignal,
): Promise<ContactImportJobDto> {
  return contactImportJobDtoSchema.parse(
    await apiPost(`/organizations/${organizationId}/contacts/import/jobs/${jobId}/cancel`, {}, signal),
  );
}

export async function previewContactMerge(
  organizationId: number,
  body: ContactMergePreviewRequest,
  signal?: AbortSignal,
): Promise<ContactMergePreviewResponse> {
  const payload = contactMergePreviewRequestSchema.parse(body);
  return contactMergePreviewResponseSchema.parse(
    await apiPost(`/organizations/${organizationId}/contacts/merge/preview`, payload, signal),
  );
}

export async function executeContactMerge(
  organizationId: number,
  body: ContactMergeExecuteRequest,
  signal?: AbortSignal,
): Promise<ContactMergeExecuteResponse> {
  const payload = contactMergeExecuteRequestSchema.parse(body);
  return contactMergeExecuteResponseSchema.parse(
    await apiPost(`/organizations/${organizationId}/contacts/merge`, payload, signal),
  );
}

export async function getProfilePhoneMatch(
  organizationId: number,
  profileId: number,
  signal?: AbortSignal,
): Promise<ContactPhoneMatchResponse> {
  return contactPhoneMatchResponseSchema.parse(
    await apiGet(
      `/organizations/${organizationId}/contact-platform-profiles/${profileId}/phone-match`,
      signal,
    ),
  );
}

export async function exportContactsCsv(
  organizationId: number,
  body: { contactIds?: number[]; query?: ContactListQuery },
  signal?: AbortSignal,
): Promise<Blob> {
  const response = await fetch(`${API_BASE_URL}/organizations/${organizationId}/contacts/export`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      contactIds: body.contactIds,
      query: body.query ? contactListQuerySchema.parse(body.query) : undefined,
    }),
    signal,
  });
  if (!response.ok) {
    throw new Error("Export failed");
  }
  return response.blob();
}

export async function addPlatformProfileAsContact(
  organizationId: number,
  profileId: number,
  body: AddPlatformProfileAsContactRequest,
  signal?: AbortSignal,
): Promise<AddPlatformProfileAsContactResponse> {
  const payload = addPlatformProfileAsContactRequestSchema.parse(body);
  return addPlatformProfileAsContactResponseSchema.parse(
    await apiPost(
      `/organizations/${organizationId}/contact-platform-profiles/${profileId}/contact`,
      payload,
      signal,
    ),
  );
}
